import { useRef, useState } from "react";
import { Camera, Upload, RefreshCw, CheckCircle2 } from "lucide-react";
import { detectSingleFace } from "../utils/faceCheck";
import CameraModal from "./CameraModal";

export default function AvatarCapture({ currentUrl, onAccept, dark = false }) {
  const fileRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const canUseCamera = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const shownUrl = preview || currentUrl;

  const accept = async (file) => {
    setError(null);
    setChecking(true);
    try {
      const result = await detectSingleFace(file);
      if (!result.ok) { setError(result.message); return; }
      setPreview(URL.createObjectURL(file));
      onAccept(file);
    } catch {
      setError("No pudimos verificar la foto. Intenta con otra imagen.");
    } finally { setChecking(false); }
  };

  const openFile = (useCamera) => {
    if (useCamera) fileRef.current.setAttribute("capture", "user");
    else fileRef.current.removeAttribute("capture");
    fileRef.current.click();
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) accept(file);
  };

  const text = dark ? "text-white/70" : "text-gray-600";
  const subtle = dark ? "text-white/40" : "text-gray-400";
  const secondaryBtn = dark
    ? "bg-white/5 border border-white/15 text-white hover:bg-white/10"
    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50";
  const ring = dark ? "border-white/15 bg-white/5" : "border-gray-200 bg-gray-100";

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <div className={`w-28 h-28 rounded-full border overflow-hidden flex items-center justify-center flex-shrink-0 ${ring}`}>
        {shownUrl
          ? <img src={shownUrl} alt="Foto de perfil" className="w-full h-full object-cover" />
          : <Camera size={28} className={subtle} />}
      </div>

      <div className="flex-1 w-full space-y-3">
        <div className="flex flex-wrap gap-2">
          {canUseCamera ? (
            <button type="button" onClick={() => setShowCamera(true)} disabled={checking}
              className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 ${secondaryBtn}`}>
              <Camera size={15} /> Usar cámara
            </button>
          ) : (
            <button type="button" onClick={() => openFile(true)} disabled={checking}
              className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 ${secondaryBtn}`}>
              <Camera size={15} /> Tomar foto
            </button>
          )}
          <button type="button" onClick={() => openFile(false)} disabled={checking}
            className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 ${secondaryBtn}`}>
            <Upload size={15} /> Subir imagen
          </button>
        </div>

        <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" />

        {checking && (
          <p className={`text-xs flex items-center gap-2 ${text}`}>
            <RefreshCw size={13} className="animate-spin" /> Verificando que la foto muestre un rostro humano…
          </p>
        )}
        {!checking && preview && !error && (
          <p className="text-xs flex items-center gap-1 text-green-600"><CheckCircle2 size={13} /> Foto verificada</p>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        <p className={`text-xs ${text}`}>Debe mostrar solo tu rostro, de frente y bien iluminado.</p>
      </div>

      {showCamera && (
        <CameraModal
          mode="face"
          onClose={() => setShowCamera(false)}
          onCapture={(file) => { setShowCamera(false); accept(file); }}
        />
      )}
    </div>
  );
}
